
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { IconButton } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import styles from './ToolbarSearch.module.css';

export interface ToolbarSearchHandle {
  focus: () => void;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  placeholder: string;
  tooltip?: string;
  pinned?: boolean;
}

export const ToolbarSearch = forwardRef<ToolbarSearchHandle, Props>(function ToolbarSearch(
  { value, onChange, open: openProp, onOpenChange, label, placeholder, tooltip, pinned = false },
  ref,
) {
  const open = pinned || openProp;
  const inputRef = useRef<HTMLInputElement>(null);
  const focusPending = useRef(false);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (open) inputRef.current?.focus();
      else {
        focusPending.current = true;
        onOpenChange(true);
      }
    },
  }));

  useEffect(() => {
    if (!open || !focusPending.current) return;
    focusPending.current = false;
    inputRef.current?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (value && !open) onOpenChange(true);
  }, [value, open, onOpenChange]);

  const toggle = () => {
    if (pinned) {
      inputRef.current?.focus();
      return;
    }
    if (!open) {
      focusPending.current = true;
      onOpenChange(true);
      return;
    }
    if (value) inputRef.current?.focus();
    else onOpenChange(false);
  };

  return (
    <span className={cn(styles.search, open && styles.searchOpen)} data-search-open={open || undefined}>
      <span className={styles.searchField} aria-hidden={!open || undefined}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            if (!value && !pinned) onOpenChange(false);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            onChange('');
            if (!pinned) onOpenChange(false);
            inputRef.current?.blur();
          }}
          placeholder={placeholder}
          aria-label={label}
          autoComplete="off"
          tabIndex={open ? undefined : -1}
          className={styles.searchInput}
        />
        {value && (
          <button
            type="button"
            className={styles.searchClear}
            aria-label="Очистить поиск"
            onClick={() => {
              onChange('');
              inputRef.current?.focus();
            }}
          >
            <X size={16} aria-hidden />
          </button>
        )}
      </span>
      <IconButton
        label={label}
        tooltip={tooltip}
        size="lg"
        aria-expanded={pinned ? undefined : open}
        onClick={toggle}
      >
        <Search size={18} aria-hidden />
      </IconButton>
    </span>
  );
});
